import { isForwardSubject } from '@hub/contracts'
import { parseHeaderBlock, type ForwardHeaders, type QuoteKind } from './email-quotes-text'

/**
 * Where the new part of an HTML email ends and its history starts (board eeqo),
 * on the sanitized body, from the markup each client writes. The history gets
 * wrapped in `.hub-quote` (a reply: collapsed by the frame's CSS) or `.hub-fwd`
 * (a forward: framed, stays open). Those class names are the app's alone:
 * `sanitizeEmailHtml` removes them from what the sender wrote.
 */

export interface HtmlQuote {
  kind: QuoteKind
  headers?: ForwardHeaders
  /** The sender wrote something besides the history. */
  hasBody: boolean
}

export interface ForwardLabels {
  forwarded: string
  from: string
  date: string
  to: string
  subject: string
}

interface Boundary {
  /** First node of the history. */
  start: Element
  kind: 'reply' | 'forward'
  /** Everything after `start` in the document is history too (Outlook writes no wrapper). */
  toEnd: boolean
  headers?: ForwardHeaders
  /** A forward's own marker and header lines, shown as the card instead. */
  remove?: Element[]
}

const GMAIL_FORWARD_RE = /^-{5,} ?(forwarded message|vidarebefordrat meddelande) ?-{5,}/i
const APPLE_FORWARD_RE = /^(begin forwarded message|vidarebefordrat brev):/i
const ORIGINAL_MESSAGE_RE = /^-{3,} ?(original message|ursprungligt meddelande) ?-{3,}$/i
const ATTRIBUTION_RE = /^(on|den) .+(wrote|skrev):?$|^.+ skrev (den )?.+:$|^le .+ a écrit ?:$/i
const HEADER_LABEL_RE = /^(from|från|sent|skickat|date|datum|to|till|cc|kopia|subject|ämne):/i
const FROM_LABEL_RE = /^(from|från):/i
const SENT_LABEL_RE = /(?:^|\s)(sent|skickat|date|datum):/i

const BLOCK_TAGS = new Set(['DIV', 'P', 'TR', 'LI', 'TABLE', 'BLOCKQUOTE', 'H1', 'H2', 'H3'])

/** Text with the line breaks the markup implies. */
function textLines(node: Node): string {
  let text = ''
  for (const child of node.childNodes) {
    if (child.nodeType === 3) text += (child.nodeValue ?? '').replace(/\s+/g, ' ')
    else if (child.nodeName === 'BR') text += '\n'
    else if (child.nodeType === 1) {
      text += textLines(child)
      if (BLOCK_TAGS.has(child.nodeName)) text += '\n'
    }
  }
  return text
}

const flatText = (node: Node) => (node.textContent ?? '').replace(/\s+/g, ' ').trim()

/** Not history inside history: a quote nested in another one never counts. */
function topLevel(body: HTMLElement, selector: string): Element[] {
  return [...body.querySelectorAll(selector)].filter(
    (el) => !el.parentElement?.closest('blockquote, .gmail_quote, .yahoo_quoted')
  )
}

function gmail(body: HTMLElement): Boundary | null {
  const start = topLevel(body, 'div.gmail_quote')[0]
  if (!start) return null
  const attribution = start.querySelector('.gmail_attr')
  const lines = attribution ? textLines(attribution) : ''
  if (GMAIL_FORWARD_RE.test(lines.trim())) {
    return {
      start,
      kind: 'forward',
      toEnd: false,
      headers: parseHeaderBlock(lines),
      remove: attribution ? [attribution] : [],
    }
  }
  return { start, kind: 'reply', toEnd: false }
}

function outlookKind(subject: string | null | undefined): 'reply' | 'forward' {
  return isForwardSubject(subject) ? 'forward' : 'reply'
}

function outlookBlock(
  start: Element,
  headerElement: Element,
  subject: string | null | undefined,
  remove: Element[]
): Boundary {
  const kind = outlookKind(subject)
  return {
    start,
    kind,
    toEnd: true,
    ...(kind === 'forward'
      ? { headers: parseHeaderBlock(textLines(headerElement)), remove: [...remove, headerElement] }
      : {}),
  }
}

/** Outlook on the web and new Outlook. */
function outlookWeb(body: HTMLElement, subject: string | null | undefined): Boundary | null {
  const block = body.querySelector('#divRplyFwdMsg')
  const append = body.querySelector('#appendonsend')
  if (!block) return append ? { start: append, kind: 'reply', toEnd: true } : null
  const rule = block.previousElementSibling?.tagName === 'HR' ? block.previousElementSibling : null
  return outlookBlock(append ?? rule ?? block, block, subject, rule ? [rule] : [])
}

/** Classic desktop Outlook (and for Mac): a bordered div around the header block. */
function outlookBorder(body: HTMLElement, subject: string | null | undefined): Boundary | null {
  const block = [...body.querySelectorAll('div[style*="border-top:solid" i]')].find((el) =>
    /border-top:\s*solid\s+#(e1e1e1|b5c4df)/i.test(el.getAttribute('style') ?? '')
  )
  return block ? outlookBlock(block, block, subject, []) : null
}

/** Word's HTML: no wrapper at all, the header is a paragraph (or the old separator). */
function outlookWord(body: HTMLElement, subject: string | null | undefined): Boundary | null {
  for (const paragraph of body.querySelectorAll('p.MsoNormal')) {
    const text = flatText(paragraph)
    if (ORIGINAL_MESSAGE_RE.test(text)) return { start: paragraph, kind: 'reply', toEnd: true }
    const bold = paragraph.querySelector('b')
    if (bold && FROM_LABEL_RE.test(flatText(bold)) && SENT_LABEL_RE.test(textLines(paragraph))) {
      return outlookBlock(paragraph, paragraph, subject, [])
    }
  }
  return null
}

function appleForward(quote: Element, marker: Element): Boundary {
  const remove = [marker]
  const headerLines: string[] = []
  for (let el = marker.nextElementSibling; el; el = el.nextElementSibling) {
    if (el.tagName === 'BR') {
      remove.push(el)
      continue
    }
    if (!HEADER_LABEL_RE.test(flatText(el))) break
    headerLines.push(flatText(el))
    remove.push(el)
  }
  return {
    start: quote,
    kind: 'forward',
    toEnd: false,
    headers: parseHeaderBlock(headerLines.join('\n')),
    remove,
  }
}

/** Apple Mail and Thunderbird. */
function citeBlockquote(body: HTMLElement): Boundary | null {
  const quote = topLevel(body, 'blockquote[type="cite"]')[0]
  if (!quote) return null
  const first = quote.firstElementChild
  if (first && APPLE_FORWARD_RE.test(flatText(first))) return appleForward(quote, first)
  const previous = quote.previousElementSibling
  const previousText = previous ? flatText(previous) : ''
  if (previous && APPLE_FORWARD_RE.test(previousText)) {
    return { start: previous, kind: 'forward', toEnd: true, remove: [previous] }
  }
  const attributed =
    previous && (previous.matches('.moz-cite-prefix') || ATTRIBUTION_RE.test(previousText))
  return { start: attributed ? previous : quote, kind: 'reply', toEnd: Boolean(attributed) }
}

function yahoo(body: HTMLElement): Boundary | null {
  const start = topLevel(body, '.yahoo_quoted')[0]
  return start ? { start, kind: 'reply', toEnd: false } : null
}

/** Any client: a blockquote with nothing after it. One in the middle is the sender's own. */
function trailingBlockquote(body: HTMLElement): Boundary | null {
  let last = body.lastElementChild
  const significant = (el: Element) =>
    !['BR', 'STYLE'].includes(el.tagName) && (flatText(el) !== '' || el.querySelector('img'))
  while (last && !significant(last)) last = last.previousElementSibling
  // `<div><blockquote/></div>`: clients love a wrapper.
  while (last && last.tagName !== 'BLOCKQUOTE' && last.children.length === 1) {
    last = last.firstElementChild
  }
  return last?.tagName === 'BLOCKQUOTE' ? { start: last, kind: 'reply', toEnd: false } : null
}

/**
 * The boundary that comes first in the document. Not the first detector to
 * hit: an Outlook reply to a Gmail mail carries a `div.gmail_quote` deep in
 * its history, and that must not leave Outlook's header block in view.
 */
function findBoundary(body: HTMLElement, subject: string | null | undefined): Boundary | null {
  const found = [
    gmail(body),
    outlookWeb(body, subject),
    outlookBorder(body, subject),
    outlookWord(body, subject),
    citeBlockquote(body),
    yahoo(body),
  ].filter((boundary): boundary is Boundary => boundary !== null)
  found.sort((a, b) => (a.start.compareDocumentPosition(b.start) & 4 ? -1 : 1))
  return found[0] ?? trailingBlockquote(body)
}

/** `start` and what follows it, up to the end of the body, in document order. */
function nodesToEnd(body: HTMLElement, start: Element): Node[] {
  const nodes: Node[] = []
  let current: Node = start
  while (current !== body && current.parentNode) {
    for (let node = current.nextSibling; node; node = node.nextSibling) nodes.push(node)
    current = current.parentNode
  }
  return [start, ...nodes]
}

function forwardHeaderCard(
  doc: Document,
  headers: ForwardHeaders | undefined,
  labels: ForwardLabels
): HTMLElement[] {
  const title = doc.createElement('p')
  title.className = 'hub-fwd-title'
  title.textContent = labels.forwarded
  const rows: Array<[string, string | undefined]> = [
    [labels.from, headers?.from],
    [labels.date, headers?.date],
    [labels.to, headers?.to],
    [labels.subject, headers?.subject],
  ]
  const present = rows.filter((row): row is [string, string] => Boolean(row[1]))
  if (present.length === 0) return [title]
  const list = doc.createElement('dl')
  list.className = 'hub-fwd-head'
  for (const [label, value] of present) {
    const term = doc.createElement('dt')
    term.textContent = label
    const detail = doc.createElement('dd')
    detail.textContent = value
    list.append(term, detail)
  }
  return [title, list]
}

/** Does the visible part say anything? (Pictures count; our placeholders do too.) */
function hasContent(body: HTMLElement, wrapper: Element): boolean {
  const clone = body.cloneNode(true) as HTMLElement
  const index = [...body.querySelectorAll('*')].indexOf(wrapper)
  clone.querySelectorAll('*')[index]?.remove()
  clone.querySelectorAll('style').forEach((style) => style.remove())
  return flatText(clone) !== '' || clone.querySelector('img') !== null
}

/** Mark the history of a sanitized body, in place. */
export function markEmailQuotes(
  body: HTMLElement,
  subject: string | null | undefined,
  labels: ForwardLabels
): HtmlQuote {
  const boundary = findBoundary(body, subject)
  if (!boundary) return { kind: 'none', hasBody: true }

  const doc = body.ownerDocument
  const nodes = boundary.toEnd ? nodesToEnd(body, boundary.start) : [boundary.start]
  const wrapper = doc.createElement('div')
  wrapper.className = boundary.kind === 'forward' ? 'hub-fwd' : 'hub-quote'
  boundary.start.parentNode!.insertBefore(wrapper, boundary.start)
  wrapper.append(...nodes)
  for (const el of boundary.remove ?? []) el.remove()
  if (boundary.kind === 'forward') {
    wrapper.prepend(...forwardHeaderCard(doc, boundary.headers, labels))
  }
  return {
    kind: boundary.kind,
    hasBody: hasContent(body, wrapper),
    ...(boundary.headers ? { headers: boundary.headers } : {}),
  }
}
